import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema } from './primitives.js';
import { HR_DEPARTMENTS, PAY_METHODS } from './hr.js';
const c = initContract();
/**
 * Payroll & Salaries (HR-02).
 *
 * EVERY route here requires payroll.* — never merely hr.view: "this screen
 * puts every salary in the company on one page" (HR-02 §9). By owner ruling
 * R15 / BR-PERM-39 payroll.view is held by the OWNER FLAG ALONE until
 * granted, and on a company's first day the Owner is the only person who can
 * open this screen at all — the intended state, not a gap.
 *
 * Workflow (AP-11): Draft → Pending approval → Approved → Posted (LOCKED) /
 * Cancelled. Reject returns to Draft with a required reason shown to the
 * preparer (BR-HR-02-10) and clears the stale approval date (defect fix).
 *
 * Approving takes BOTH gates (BR-HR-02-08): the approver dropdown is BUILT
 * from employees carrying the Approver job role (routing), and the decision
 * needs hr.approve (capability) — the same two-gate model as leave. It also
 * needs payroll.view, which is not a third opinion about who may decide but
 * BR-PERM-40 about what may be READ: the approve and reject responses are the
 * whole run, every employee named with their base, allowances, deductions and
 * net, and "no export may carry them to somebody without payroll.view".
 *
 * Posting (BR-HR-02-11, §12): payroll.approve AND accounting.approve.
 * Writes, in ONE transaction against the seeded chart —
 *   A. the salary journal entry: DR 5110 gross · CR 2140 net · CR 2120
 *      deductions withheld (§12's own table; every code verified to exist);
 *   B. the payment leg(s): DR 2140 · CR the salary bank account's ledger
 *      account (cash → 1111 Petty Cash) — one entry per employee or one bulk
 *      entry, per the voucher mode.
 * Amounts convert to base at each line's fx rate before totalling
 * (BR-HR-02-07). The rate is the FIN-05 convention — units of the foreign
 * currency per ONE base unit, so conversion DIVIDES (BR-FIN-05-02, F-10) —
 * defaulted from the rate table at run creation, per-line overridable.
 */
export const PAYROLL_STATUSES = [
    'draft',
    'pending_approval',
    'approved',
    'posted',
    'cancelled',
];
export const PAYROLL_MESSAGES = {
    /** BR-HR-02-01. */
    incomplete: 'A run must have a start date, an end date and a pay date.',
    /** Q-HR-02-2. */
    duplicatePeriod: 'A run for this period already exists. Cancel it first, or pick another period.',
    /** BR-HR-02-08. */
    approveDenied: 'You do not have permission to approve a payroll run. Ask an administrator to grant it.',
    /** Routing half — the named approver (or the owner) decides. */
    notRouted: (approver) => `This run is routed to ${approver}. Only they (or the owner) can decide it.`,
    approverRequired: 'Choose an approver before submitting. The list is built from the Approver job role.',
    approverNotApprover: 'The chosen approver no longer carries the Approver job role.',
    /** §6's post hint — ERR-HR-02-01's sentence, under the greyed Post button. */
    postHint: 'The run must be approved before it can be posted.',
    /** BR-HR-02-14. */
    negativeNet: (name) => `${name} has a negative net pay. Fix the deductions before posting.`,
    /** The no-bank-account defect fix: warn AND block. */
    noBankAccount: (name) => `${name} is paid by bank transfer but has no salary bank account. Set one, or change the method.`,
    /** The §7 locked notices — verbatim. */
    postedLocked: 'Payroll posted and locked. Payment vouchers and the salary journal entry were generated; the bank account was debited.',
    cancelledLocked: 'This payroll run is cancelled and read-only.',
    /** The two §7 posting confirmations — verbatim. */
    postedPerEmployee: 'Generated one Payment Voucher per included employee, plus the salary journal entry.',
    postedBulk: 'Generated a single bulk Payment Voucher with per-employee allocation, plus the salary journal entry.',
    payslipsGenerated: (count) => `Generated ${count} payslip${count === 1 ? '' : 's'}.`,
    /**
     * BR-HR-02-13 / ERR-HR-02-07. The prototype's Generate payslips button was
     * greyed before posting and FIRED ANYWAY; this is the server refusal that
     * makes the grey mean something.
     */
    payslipsBeforePosting: 'Payslips are generated only after posting.',
    /** ERR-HR-02-05 — the reason box the prototype never drew on a rejection. */
    rejectReasonRequired: 'Why this run was sent back for correction…',
    /**
     * BR-HR-02-12 / R8 / ERR-HR-02-08 — "in every field" is the point: the
     * prototype left the period dates editable after posting, which silently
     * restates a run that has already hit the ledger.
     */
    locked: 'This run is posted and locked in every field. Correct it by reversing, never by rewriting.',
    notDraft: 'Only a draft run can be edited or submitted.',
    notPending: 'Only a run pending approval can be decided.',
};
/**
 * D46 — HR-02's three parameterised payroll refusals, structured. `notRouted`
 * carries the routed approver's name; `negativeNet` and `noBankAccount` an
 * employee name. No token, no plural. Keys sit on the payroll screen's `hr.*`
 * namespace. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const PAYROLL_REFUSALS = {
    notRouted: (approver) => ({
        message: PAYROLL_MESSAGES.notRouted(approver),
        key: 'hr.errNotRouted',
        params: { approver },
    }),
    negativeNet: (name) => ({
        message: PAYROLL_MESSAGES.negativeNet(name),
        key: 'hr.errNegativeNet',
        params: { name },
    }),
    noBankAccount: (name) => ({
        message: PAYROLL_MESSAGES.noBankAccount(name),
        key: 'hr.errNoBankAccount',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const PayrollLineSchema = z
    .object({
    id: z.uuid(),
    employeeId: z.uuid(),
    code: z.string(),
    fullName: z.string(),
    fullNameAr: z.string(),
    department: z.enum(HR_DEPARTMENTS),
    /** BR-HR-02-03: on_leave/suspended listed but unticked by default. */
    employeeStatus: z.string(),
    included: z.boolean(),
    baseSalary: z.string(),
    allowances: z.string(),
    deductions: z.string(),
    /** base + allowances − deductions, employee's own currency (BR-HR-02-05). */
    net: z.string(),
    currency: z.string(),
    fxRate: z.string(),
    paymentMethod: z.enum(PAY_METHODS),
    bankAccountId: z.uuid().nullable(),
    bankAccountName: z.string().nullable(),
})
    .strict();
export const PayrollRunSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    runNo: z.number().int(),
    startDate: z.string(),
    endDate: z.string(),
    payDate: z.string(),
    department: z.enum(HR_DEPARTMENTS).nullable(),
    status: z.enum(PAYROLL_STATUSES),
    approverEmployeeId: z.uuid().nullable(),
    approverName: z.string().nullable(),
    approvalDate: z.string().nullable(),
    rejectionReason: z.string().nullable(),
    voucherMode: z.enum(['per_employee', 'bulk']),
    notes: z.string().nullable(),
    postedAt: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    payslipsGeneratedAt: z.string().nullable(),
    /**
     * OPTIONAL, and the option is BR-PERM-40 rather than a convenience.
     *
     * Approve and reject are guarded by `hr.approve` (BR-HR-02-08's capability
     * half), which under BR-PERM-40 buys no right to a pay figure at all: "no
     * export may carry them to somebody without payroll.view". So when the
     * decider is an HR approver who does not hold `payroll.view`, these two
     * keys are ABSENT — the SEE_ITEM_COST convention, "absent from the response
     * rather than masked in it", because a number that reaches the browser is
     * a number the browser can be made to show.
     *
     * Every `payroll.*`-guarded route (get, create, update, submit, post,
     * cancel) always carries them: the guard has already paid for them.
     */
    lines: PayrollLineSchema.array().optional(),
    /** Ticked rows only, each converted at its fx rate (BR-HR-02-06/-07). */
    totals: z
        .object({
        currency: z.string(),
        base: z.string(),
        allowances: z.string(),
        deductions: z.string(),
        net: z.string(),
        includedCount: z.number().int(),
    })
        .strict()
        .optional(),
    createdAt: z.string(),
})
    .strict();
export const PayrollRunSummarySchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    payDate: z.string(),
    department: z.enum(HR_DEPARTMENTS).nullable(),
    status: z.enum(PAYROLL_STATUSES),
    includedCount: z.number().int(),
    netTotal: z.string(),
})
    .strict();
export const PayslipSchema = z
    .object({
    runId: z.uuid(),
    runCode: z.string(),
    payDate: z.string(),
    employeeId: z.uuid(),
    code: z.string(),
    fullName: z.string(),
    fullNameAr: z.string(),
    department: z.enum(HR_DEPARTMENTS),
    baseSalary: z.string(),
    allowances: z.string(),
    deductions: z.string(),
    net: z.string(),
    currency: z.string(),
    paymentMethod: z.enum(PAY_METHODS),
})
    .strict();
export const CreatePayrollRunRequestSchema = z
    .object({
    startDate: IsoDateSchema,
    endDate: IsoDateSchema,
    payDate: IsoDateSchema,
    department: z.enum(HR_DEPARTMENTS).optional(),
})
    .strict();
export const UpdatePayrollRunRequestSchema = z
    .object({
    payDate: IsoDateSchema.optional(),
    approverEmployeeId: z.uuid().nullable().optional(),
    voucherMode: z.enum(['per_employee', 'bulk']).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    /** The tick + this period's figures, per line (BR-HR-02-04's overrides). */
    lines: z
        .object({
        employeeId: z.uuid(),
        included: z.boolean().optional(),
        allowances: MoneyStringSchema.optional(),
        deductions: MoneyStringSchema.optional(),
        /** FIN-05 convention: units per one base unit — must be positive. */
        fxRate: z
            .string()
            .regex(/^\d{1,9}(\.\d{1,6})?$/)
            .refine((value) => !/^0+(\.0+)?$/.test(value), 'A rate must be greater than zero.')
            .optional(),
    })
        .strict()
        .array()
        .optional(),
})
    .strict();
const runParams = z.object({ runId: z.uuid() }).strict();
export const payrollContract = c.router({
    list: {
        method: 'GET',
        path: '/api/hr/payroll/runs',
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('runs', PayrollRunSummarySchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Past runs (payroll.view — the Owner alone until granted, R15), PAGED (NFR-42): a register that gains a row every month for ever, and each row costs a read of its own lines.',
    },
    get: {
        method: 'GET',
        path: '/api/hr/payroll/runs/:runId',
        pathParams: runParams,
        responses: { 200: PayrollRunSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'One run (payroll.view). While DRAFT the lines re-sync to the CURRENT employee list and statuses (BR-HR-02-15): terminated vanish, new joiners appear, on-leave untick.',
    },
    create: {
        method: 'POST',
        path: '/api/hr/payroll/runs',
        body: CreatePayrollRunRequestSchema,
        responses: {
            201: PayrollRunSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Start a run (payroll.edit): PR-#### allocated; lines built from non-terminated employees in scope (BR-HR-02-02), active ticked / on-leave unticked (BR-HR-02-03), figures pre-filled with percent deductions resolved against base (BR-HR-02-04). One live run per period (Q-HR-02-2).',
    },
    update: {
        method: 'PATCH',
        path: '/api/hr/payroll/runs/:runId',
        pathParams: runParams,
        body: UpdatePayrollRunRequestSchema,
        responses: {
            200: PayrollRunSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT run (payroll.edit): ticks, this period’s figures, approver, voucher mode, notes.',
    },
    submit: {
        method: 'POST',
        path: '/api/hr/payroll/runs/:runId/submit',
        pathParams: runParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PayrollRunSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Submit for approval (payroll.edit): needs a named approver who carries the Approver job role (BR-HR-02-08 routing half).',
    },
    approve: {
        method: 'POST',
        path: '/api/hr/payroll/runs/:runId/approve',
        pathParams: runParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PayrollRunSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Approve (hr.approve AND the routed approver — both gates, BR-HR-02-08 — AND payroll.view, because the response carries every line’s pay: BR-PERM-40, "no export may carry them to somebody without payroll.view"). Stamps the approval date.',
    },
    reject: {
        method: 'POST',
        path: '/api/hr/payroll/runs/:runId/reject',
        pathParams: runParams,
        body: z.object({ reason: z.string().trim().min(1).max(500) }).strict(),
        responses: {
            200: PayrollRunSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reject with a REQUIRED reason shown to the preparer (BR-HR-02-10) — same three gates as approve, payroll.view included, for the same BR-PERM-40 reason. Returns to Draft and clears the stale approval date (defect fix).',
    },
    post: {
        method: 'POST',
        path: '/api/hr/payroll/runs/:runId/post',
        pathParams: runParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ run: PayrollRunSchema, message: z.string() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post (payroll.approve AND accounting.approve): only from Approved, never twice (BR-HR-02-09); negative net blocks (BR-HR-02-14); bank-transfer with no account blocks (defect fix). Writes the §12 entries — DR 5110 gross / CR 2140 net / CR 2120 withheld, then DR 2140 / CR the bank legs per the voucher mode — and locks the run for ever (R8).',
    },
    cancel: {
        method: 'POST',
        path: '/api/hr/payroll/runs/:runId/cancel',
        pathParams: runParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PayrollRunSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Void an unposted run (payroll.edit). Cancelled is read-only for ever.',
    },
    payslips: {
        method: 'POST',
        path: '/api/hr/payroll/runs/:runId/payslips',
        pathParams: runParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ payslips: PayslipSchema.array(), message: z.string() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Generate payslips — ONLY after posting (BR-HR-02-13; the styled-disabled-but-fires prototype button is the defect). One per included employee (Q-HR-02-3). payroll.edit.',
    },
    getPayslips: {
        method: 'GET',
        path: '/api/hr/payroll/runs/:runId/payslips',
        pathParams: runParams,
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('payslips', PayslipSchema),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Read the generated payslips (payroll.view), PAGED (NFR-42) — one row per INCLUDED employee, so the batch is headcount-sized. 409 until generated.',
    },
    approvers: {
        method: 'GET',
        path: '/api/hr/payroll/approvers',
        responses: {
            200: z
                .object({
                approvers: z
                    .object({
                    employeeId: z.uuid(),
                    fullName: z.string(),
                    fullNameAr: z.string(),
                    jobTitle: z.string(),
                })
                    .strict()
                    .array(),
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The approver dropdown (payroll.view): BUILT from employees carrying the Approver job role, live — tick the role in HR-01 and the person appears here at once (BR-HR-02-08).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=payroll.js.map